import PageHeader from "@/components/PageHeader";
import { DataTableQueryContextProvider } from "@/components/data-table/DataTableQueryContext";
import { RowSelectionProvider } from "@/components/data-table/context/RowSelectionProvider";
import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import { QueryTable } from "@/components/query-table/QueryTable";
import { Module } from "@/constants";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { displayProjectStorePeriod } from "@/schemas/projectStorePeriod";
import { FetchListRequest } from "@/services/api";
import { ReadProjectStorePeriod } from "@/types/projectStorePeriod";
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query";
import { ColumnDef } from "@tanstack/react-table";
import { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCheckIsProjectLocked } from "../hooks/useCheckIsProjectLocked";
import { useProjectBreadcrumb } from "../hooks/useProjectBreadcrumb";
import { ProjectStorePeriodsService } from "../service/projectStorePeriod";
import AssignStoresToProjectsMenubarItem from "./AssignStoresToProjectsMenubarItem";
import ClientStoreNumberField from "./ClientStoreNumberField";
import { ProjectStoreProvider } from "./ProjectStoreContext";
import ProjectStoreCreator from "./ProjectStoreCreator";
import { ProjectStoreListContent } from "./ProjectStoreListContent";
import RemoveStoreButton from "./RemoveStoreButton";
import StoreGroupFilter from "./StoreGroupFilter";
import { ProjectStoreResetMenuBarMenu } from "./ProjectStoreResetMenuBarMenu";

const ProjectStoreList = () => {
  const name = "projectStorePeriods";
  const { t } = useTranslation();
  const projectId = useProjectId();
  const projectQuery = useProjectQuery(projectId);
  const options = useCreateDataTableOptions<ReadProjectStorePeriod>(name);
  const queryKey = useDynamicQueryParams(name, options);
  const { can_update } = useModulePermissions();
  const isProjectLocked = useCheckIsProjectLocked();
  const breadcrumbItems = useProjectBreadcrumb(projectQuery, [
    {
      label: t("projects.breadcrumb.stores"),
    },
  ]);

  const fetchList = (fetchParams: FetchListRequest<ReadProjectStorePeriod>) => {
    return ProjectStorePeriodsService.getList(projectId, fetchParams);
  };

  const listQuery = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchList({
        lastItem: pageParam,
        filters: options.filters,
        sorting: options.sorting[0],
      }),
    initialPageParam: undefined,
    getNextPageParam: ({ last_item }) => last_item,
    refetchOnWindowFocus: false,
    placeholderData: keepPreviousData,
  });

  const queryValue = useMemo(() => ({ query: listQuery }), [listQuery]);
  const tableFeatures = useMemo(
    () => ({
      enableRowSelection: false,
      enableSelectAll: false,
      initialColumnSizes: {
        actions: 300,
      },
      exportSettings: {
        moduleName: Module.ProjectStores,
        queryParams: {
          project_id: projectId,
        },
      },
    }),
    [projectId],
  );

  const columnOverrides = useMemo(
    (): Record<string, ColumnDef<ReadProjectStorePeriod>> => ({
      client_store_number: {
        id: "client_store_number",
        cell: ({ row }) => (
          <ClientStoreNumberField
            row={row}
            disabled={!can_update || isProjectLocked}
          />
        ),
      },
      actions: {
        id: "actions",
        cell: ({ row }) => (
          <RemoveStoreButton
            row={row}
            disabled={!can_update || isProjectLocked}
          />
        ),
      },
    }),
    [can_update, isProjectLocked],
  );

  return (
    <>
      <RowSelectionProvider>
        <QueryTable<ReadProjectStorePeriod>
          schema={displayProjectStorePeriod}
          name={name}
          query={listQuery}
          options={options}
          tableFeatures={tableFeatures}
          columnOverrides={columnOverrides}
        >
          <PageHeader
            breadcrumbItems={breadcrumbItems}
            title={t("projects.breadcrumb.stores")}
          />
          <ProjectDetailsAndNavigation />
          <DataTableQueryContextProvider value={queryValue}>
            <ProjectStoreProvider>
              <div className="flex flex-row gap-2 justify-between items-center py-2">
                <QueryTable.Menubar className="mb-0 grow">
                  <QueryTable.Menubar.Tools>
                    <AssignStoresToProjectsMenubarItem />
                  </QueryTable.Menubar.Tools>
                  <QueryTable.Menubar.Columns />
                  <ProjectStoreResetMenuBarMenu />
                </QueryTable.Menubar>

                <StoreGroupFilter />
              </div>
              <ProjectStoreListContent />
              <ProjectStoreCreator />
            </ProjectStoreProvider>
          </DataTableQueryContextProvider>
        </QueryTable>
      </RowSelectionProvider>
    </>
  );
};

export default memo(ProjectStoreList);
